import { NavLink } from 'react-router-dom';
import type { Capability, Engine } from '@/engines/registry';
import { cn } from '@/lib/utils';

/* Contextual capability nav — the active engine's capabilities only (§5.1).
 * On desktop (>= lg) it is the fixed 208px column (unchanged). Below lg it is
 * hidden and rendered via `variant="drawer"` inside the MobileDrawer as a
 * full-width list with >= 44px tap targets. Unrouted capabilities render as
 * disabled "soon" items; a capability with `reservedNote` renders an honest
 * reserved tile; `children` render as an indented subnav under their parent. */

function CapabilityItem({
  c,
  engineKey,
  drawer,
  onNavigate,
}: {
  c: Capability;
  engineKey: string;
  drawer?: boolean;
  onNavigate?: () => void;
}) {
  // Drawer-only extras: larger tap target, never applied on desktop.
  const tap = drawer ? 'flex min-h-[44px] items-center ' : '';
  if (c.path) {
    return (
      <NavLink
        to={c.path}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            tap,
            'rounded-md px-2.5 py-1.5 text-sm transition-colors duration-fast',
            isActive
              ? 'bg-engine-wash font-medium text-engine'
              : 'text-text-secondary hover:bg-hover hover:text-text-primary',
          )
        }
        data-engine={engineKey}
      >
        {c.label}
      </NavLink>
    );
  }
  if (c.reservedNote) {
    return (
      <span className="rounded-md px-2.5 py-1.5" title={c.reservedNote}>
        <span className="flex items-center justify-between text-sm text-text-tertiary">
          {c.label}
          <span className="text-[10px] uppercase">reserved</span>
        </span>
        <span className="mt-0.5 block text-[11px] leading-snug text-text-tertiary">
          {c.reservedNote}
        </span>
      </span>
    );
  }
  return (
    <span
      className="flex items-center justify-between rounded-md px-2.5 py-1.5 text-sm text-text-tertiary"
      title="Coming soon"
    >
      {c.label}
      <span className="text-[10px] uppercase">soon</span>
    </span>
  );
}

export function CapabilityNav({
  engine,
  variant = 'rail',
  onNavigate,
}: {
  engine: Engine;
  variant?: 'rail' | 'drawer';
  onNavigate?: () => void;
}) {
  const drawer = variant === 'drawer';
  return (
    <nav
      aria-label={`${engine.name} capabilities`}
      className={
        drawer
          ? 'flex w-full flex-col gap-0.5 p-2'
          : 'hidden w-52 shrink-0 flex-col gap-0.5 border-r border-border-subtle bg-surface p-3 lg:flex'
      }
    >
      <div className="mb-2 px-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-engine" data-engine={engine.key}>
          {engine.name}
        </div>
        <div className="mt-0.5 text-xs text-text-tertiary">{engine.tagline}</div>
      </div>
      {engine.capabilities.map((c) => (
        <div key={c.key} className="flex flex-col gap-0.5">
          <CapabilityItem c={c} engineKey={engine.key} drawer={drawer} onNavigate={onNavigate} />
          {c.children && c.children.length > 0 && (
            <div className="ml-2.5 flex flex-col gap-0.5 border-l border-border-subtle pl-1.5">
              {c.children.map((child) => (
                <CapabilityItem
                  key={child.key}
                  c={child}
                  engineKey={engine.key}
                  drawer={drawer}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}
